import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { sinceQuery } from '@/api/localDay';
import { useAuth } from '@/auth/AuthProvider';
import type { TodaySnapshotDto } from '@/api/types';

/**
 * App Badging API: surface a single dot/numeral on the installed PWA's
 * dock/taskbar/home-screen icon when there's something the user should
 * attend to today. Currently signals "no wellbeing check-in yet today".
 *
 * Cleared when a wellbeing entry exists for the current calendar day, or
 * when the user signs out (snapshot becomes unavailable).
 *
 * No-ops in browsers without the Badging API (Safari, Firefox).
 */
type BadgeNav = Navigator & {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

function isToday(ts: string): boolean {
  const d = new Date(ts);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

export function useAppBadge() {
  const { isAuthenticated } = useAuth();
  // Reuse the same ['today'] cache key as useTodaySnapshot so we don't
  // double-fetch when Today is also mounted; just gate on auth so we
  // don't trigger a 401 redirect from anonymous routes.
  const today = useQuery({
    queryKey: ['today'],
    queryFn: ({ signal }) =>
      api.get<TodaySnapshotDto>(`/api/today?${sinceQuery()}`, { signal }),
    enabled: isAuthenticated,
  });

  useEffect(() => {
    const nav = navigator as BadgeNav;
    if (!nav.setAppBadge || !nav.clearAppBadge) return;

    if (!isAuthenticated) {
      void nav.clearAppBadge().catch(() => {});
      return;
    }

    const data = today.data;
    if (!data) return;

    const hasCheckInToday =
      data.latestWellbeing !== null && isToday(data.latestWellbeing.timestamp);

    if (hasCheckInToday) {
      void nav.clearAppBadge().catch(() => {});
    } else {
      void nav.setAppBadge(1).catch(() => {});
    }
  }, [isAuthenticated, today.data]);
}
